/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { useContext, useMemo } from "react"
import { useSelector } from "react-redux"
import { TroyWebTracker } from "utils/tracking"
import { AppState } from "store/store.type"
import {
  EventAction,
  EventCategory,
  editType,
  itemsEdited,
  merchantType,
} from "constants/tracking"
import {
  ACTION_TYPE,
  ExportMenuItemProperty,
} from "types/delv-platform-api/inventoryCategory"
import { BULK_UPLOAD_TYPE, BulkUploadContext } from "../BulkUploadContext"

export enum SCRIBE_BULK_UPLOAD_LOADING_STATUS {
  SUCCESS = "SUCCESS",
  FAILURE = "FAILURE",
}

export const useScribeUploadAndPublishDrawer = () => {
  const {
    merchantGroupID,
    merchantID,
    outlet,
    outletV2,
    menuGroupDetail,
    enableTroyNewMerchantEndpoint,
  } = useSelector((state: AppState) => state.menu)

  const { bulkUploadType, actionType } = useContext(BulkUploadContext)

  return useMemo(() => {
    const category =
      bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY
        ? EventCategory.bulkEditinventoryTab
        : EventCategory.bulkEditmenuTab

    const downloadCatalogueCategory =
      bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY
        ? EventCategory.bulkEditDownloadcatalogueTab
        : EventCategory.bulkEditMenuDownloadcatalogue

    const getMerchantType = () => {
      if (bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY) return undefined
      if (merchantGroupID) return merchantType.multi
      return merchantType.single
    }

    const getEditType = () => {
      if (bulkUploadType === BULK_UPLOAD_TYPE.INVENTORY) return undefined
      if (actionType === ACTION_TYPE.CREATE) return editType.create
      return editType.edit
    }

    const getBusinessType = () => {
      if (menuGroupDetail?.menuGroupID) {
        return menuGroupDetail.businessType ?? 0
      }
      const currentOutlet = enableTroyNewMerchantEndpoint ? outletV2 : outlet
      return currentOutlet?.businessType ?? 0
    }

    const labels = {
      MERCHANT_GROUP_ID: merchantGroupID,
      STORE_ID: merchantID,
      MERCHANT_TYPE: getMerchantType(),
      EDIT_TYPE: getEditType(),
      BUSINESS_TYPE: getBusinessType(),
      MENU_GROUP_ID: menuGroupDetail?.menuGroupID,
    }

    return {
      trackPageView: () => {
        TroyWebTracker.sendEvent({
          category,
          action: EventAction.pageView,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
          },
        })
      },
      trackDownloadCSVClick: () => {
        TroyWebTracker.sendEvent({
          category,
          action: EventAction.clickDownloadcsv,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
          },
        })
      },
      trackPrepareBulkClick: () => {
        TroyWebTracker.sendEvent({
          category,
          action: EventAction.clickPreparebulk,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
          },
        })
      },
      trackExitClick: () => {
        TroyWebTracker.sendEvent({
          category,
          action: EventAction.clickExit,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
          },
        })
      },
      trackDragReplaceFile: (
        loadStatus: keyof typeof SCRIBE_BULK_UPLOAD_LOADING_STATUS,
      ) => {
        TroyWebTracker.sendEvent({
          category,
          action: EventAction.dragReplaceFile,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
            LOADING_STATUS: loadStatus,
          },
        })
      },
      trackUploadItemsClick: (
        loadStatus: keyof typeof SCRIBE_BULK_UPLOAD_LOADING_STATUS,
      ) => {
        TroyWebTracker.sendEvent({
          category,
          action: EventAction.clickUploaditems,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
            LOADING_STATUS: loadStatus,
          },
        })
      },
      trackDownloadCataloguePageView: () => {
        TroyWebTracker.sendEvent({
          category: downloadCatalogueCategory,
          action: EventAction.pageView,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
          },
        })
      },
      trackDownloadCSVWithStatusClick: (
        loadStatus: keyof typeof SCRIBE_BULK_UPLOAD_LOADING_STATUS,
        properties?: ExportMenuItemProperty[],
        items?: string[],
        checkAll?: boolean,
      ) => {
        const isMenuEdit =
          actionType === ACTION_TYPE.UPDATE &&
          bulkUploadType === BULK_UPLOAD_TYPE.MENU

        TroyWebTracker.sendEvent({
          category: downloadCatalogueCategory,
          action: EventAction.clickDownloadcsv,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
            LOADING_STATUS: loadStatus,
            ...(isMenuEdit && {
              ITEM_PROPERTIES_EDITED: properties,
              ITEM_IDS: items,
              ITEMS_EDITED: checkAll
                ? itemsEdited.allItems
                : itemsEdited.partialItems,
            }),
          },
        })
      },
      trackBackClick: () => {
        TroyWebTracker.sendEvent({
          category: downloadCatalogueCategory,
          action: EventAction.clickBack,
          labels: {
            ...labels,
            TIMESTAMP: new Date().valueOf(),
            LOADING_STATUS: SCRIBE_BULK_UPLOAD_LOADING_STATUS.SUCCESS,
          },
        })
      },
    }
  }, [
    bulkUploadType,
    merchantGroupID,
    merchantID,
    actionType,
    outlet,
    outletV2,
    menuGroupDetail,
    enableTroyNewMerchantEndpoint,
  ])
}
